import React, { useState } from 'react';
import {
    Alert,
    Button,
    Modal,
    Popconfirm,
    Segmented,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    CheckCircleOutlined,
    DownloadOutlined,
    LinkOutlined,
    MailOutlined,
    PaperClipOutlined,
    StopOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useAssignCrmInboundEmailMutation,
    useGetCrmCompaniesQuery,
    useGetCrmInboxQuery,
    useIgnoreCrmInboundEmailMutation,
    useLazyGetCrmInboundAttachmentUrlQuery,
    useRunCrmInboxSyncMutation,
} from '../../../services/nestjs-base-api';
import {
    ICrmInboundEmail,
    ICrmInboxSyncResult,
} from '../../../services/nestjs-base-api/types';
import { useAuth } from '../../../hooks/useAuth';
import { EmailBodyHtml } from './CrmRichTextEditor';

const { Text } = Typography;

const MATCH_METHOD_LABEL: Record<string, string> = {
    message_id: 'Reply thread',
    contact_email: 'Contact',
    platform_user: 'Platform user',
    dist_list: 'Prior recipient',
    domain: 'Domain',
    subject: 'Subject',
    manual: 'Manual',
};

/**
 * SYR-92 Phase 3 — review queue for the hello@ mailbox: sync on demand, see
 * where each inbound email landed (or should land), fix the stragglers.
 */
export const CrmInboxTab: React.FC<{
    onOpenCompany: (companyId: number) => void;
}> = ({ onOpenCompany }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    const [status, setStatus] = useState<string>('flagged');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [lastSync, setLastSync] = useState<ICrmInboxSyncResult | null>(null);
    const [assignTarget, setAssignTarget] = useState<ICrmInboundEmail | null>(null);
    const [assignCompanyId, setAssignCompanyId] = useState<number | null>(null);
    const [companySearch, setCompanySearch] = useState('');
    const [viewTarget, setViewTarget] = useState<ICrmInboundEmail | null>(null);
    const [showFullBody, setShowFullBody] = useState(false);

    const { data: emails, isFetching } = useGetCrmInboxQuery({
        jwtToken,
        status: status === 'all' ? undefined : status,
    });
    const { data: companySearchResults, isFetching: companiesLoading } =
        useGetCrmCompaniesQuery(
            { jwtToken, q: companySearch, limit: 20, includeArchived: false },
            { skip: !assignTarget },
        );
    const [runSync, runSyncResult] = useRunCrmInboxSyncMutation();
    const [assign, assignResult] = useAssignCrmInboundEmailMutation();
    const [ignore] = useIgnoreCrmInboundEmailMutation();
    const [getAttachmentUrl] = useLazyGetCrmInboundAttachmentUrlQuery();

    const handleSync = async () => {
        try {
            const result = await runSync({ jwtToken }).unwrap();
            setLastSync(result);
        } catch (e: any) {
            message.error(e?.data?.message || 'Sync failed');
        }
    };

    const handleAssign = async () => {
        if (!assignTarget || !assignCompanyId) return;
        try {
            await assign({ jwtToken, id: assignTarget.id, companyId: assignCompanyId }).unwrap();
            message.success(intl.formatMessage({ id: 'crm.inbox.assigned' }));
            setAssignTarget(null);
            setAssignCompanyId(null);
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to assign');
        }
    };

    const handleQuickAssign = async (email: ICrmInboundEmail, companyId: number) => {
        try {
            await assign({ jwtToken, id: email.id, companyId }).unwrap();
            message.success(intl.formatMessage({ id: 'crm.inbox.assigned' }));
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to assign');
        }
    };

    const handleIgnore = async (ids: number[]) => {
        try {
            for (const id of ids) {
                await ignore({ jwtToken, id }).unwrap();
            }
            setSelectedIds([]);
            message.success(
                intl.formatMessage({ id: 'crm.inbox.ignored' }, { count: ids.length }),
            );
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to ignore');
        }
    };

    const handleDownload = async (email: ICrmInboundEmail, index: number) => {
        try {
            const { url } = await getAttachmentUrl({ jwtToken, id: email.id, index }).unwrap();
            window.open(url, '_blank', 'noopener');
        } catch (e: any) {
            message.error(e?.data?.message || 'Attachment unavailable');
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="crm.inbox.received" defaultMessage="Received" />,
            dataIndex: 'receivedAt',
            width: 110,
            render: (v: string) => new Date(v).toLocaleDateString(),
            sorter: (a: ICrmInboundEmail, b: ICrmInboundEmail) =>
                a.receivedAt.localeCompare(b.receivedAt),
        },
        {
            title: <FormattedMessage id="crm.inbox.from" defaultMessage="From" />,
            key: 'from',
            render: (_: unknown, r: ICrmInboundEmail) => (
                <div>
                    {r.fromName && <div>{r.fromName}</div>}
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.fromEmail}</Text>
                </div>
            ),
        },
        {
            title: <FormattedMessage id="crm.inbox.subject" defaultMessage="Subject" />,
            key: 'subject',
            render: (_: unknown, r: ICrmInboundEmail) => (
                <Space size={6}>
                    <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, whiteSpace: 'normal', textAlign: 'left' }}
                        onClick={() => {
                            setViewTarget(r);
                            setShowFullBody(false);
                        }}
                    >
                        {r.subject || <FormattedMessage id="crm.inbox.noSubject" defaultMessage="(no subject)" />}
                    </Button>
                    {(r.attachmentsJson?.length ?? 0) > 0 && (
                        <Tag icon={<PaperClipOutlined />}>{r.attachmentsJson!.length}</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="crm.inbox.match" defaultMessage="Account" />,
            key: 'match',
            render: (_: unknown, r: ICrmInboundEmail) => {
                if (r.matchStatus === 'matched' && r.crmCompany) {
                    return (
                        <Space size={4} wrap>
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0 }}
                                icon={<LinkOutlined />}
                                onClick={() => onOpenCompany(r.crmCompany!.id)}
                            >
                                {r.crmCompany.name}
                            </Button>
                            <Tag>{MATCH_METHOD_LABEL[r.matchMethod || ''] || r.matchMethod}</Tag>
                        </Space>
                    );
                }
                if (r.matchStatus === 'ignored') {
                    return (
                        <Tooltip title={r.ignoreReason}>
                            <Tag icon={<StopOutlined />}>
                                <FormattedMessage id="crm.inbox.statusIgnored" defaultMessage="Ignored" />
                            </Tag>
                        </Tooltip>
                    );
                }
                if ((r.candidatesJson?.length ?? 0) > 0) {
                    return (
                        <Space size={4} wrap>
                            {r.candidatesJson!.slice(0, 3).map((c) => (
                                <Tooltip key={c.companyId} title={c.reason}>
                                    <Tag
                                        color="orange"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleQuickAssign(r, c.companyId)}
                                    >
                                        {c.name} ?
                                    </Tag>
                                </Tooltip>
                            ))}
                        </Space>
                    );
                }
                return (
                    <Tag color="orange">
                        <FormattedMessage id="crm.inbox.unmatched" defaultMessage="Unmatched" />
                    </Tag>
                );
            },
        },
        {
            title: '',
            key: 'actions',
            width: 180,
            render: (_: unknown, r: ICrmInboundEmail) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={() => {
                            setAssignTarget(r);
                            setAssignCompanyId(r.crmCompanyId);
                            setCompanySearch('');
                        }}
                    >
                        <FormattedMessage id="crm.inbox.assign" defaultMessage="Assign" />
                    </Button>
                    {r.matchStatus !== 'ignored' && (
                        <Popconfirm
                            title={<FormattedMessage id="crm.inbox.ignoreConfirm" defaultMessage="Ignore this email?" />}
                            onConfirm={() => handleIgnore([r.id])}
                        >
                            <Button size="small" icon={<StopOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }} wrap>
                <Button
                    type="primary"
                    icon={<SyncOutlined spin={runSyncResult.isLoading} />}
                    loading={runSyncResult.isLoading}
                    onClick={handleSync}
                >
                    <FormattedMessage id="crm.inbox.syncNow" defaultMessage="Sync now" />
                </Button>
                <Segmented
                    value={status}
                    onChange={(v) => setStatus(v as string)}
                    options={[
                        { value: 'flagged', label: intl.formatMessage({ id: 'crm.inbox.filterFlagged' }) },
                        { value: 'matched', label: intl.formatMessage({ id: 'crm.inbox.filterMatched' }) },
                        { value: 'ignored', label: intl.formatMessage({ id: 'crm.inbox.filterIgnored' }) },
                        { value: 'all', label: intl.formatMessage({ id: 'crm.inbox.filterAll' }) },
                    ]}
                />
                {selectedIds.length > 0 && (
                    <Popconfirm
                        title={
                            <FormattedMessage
                                id="crm.inbox.bulkIgnoreConfirm"
                                defaultMessage="Ignore {count} emails?"
                                values={{ count: selectedIds.length }}
                            />
                        }
                        onConfirm={() => handleIgnore(selectedIds)}
                    >
                        <Button danger icon={<StopOutlined />}>
                            <FormattedMessage
                                id="crm.inbox.bulkIgnore"
                                defaultMessage="Ignore selected ({count})"
                                values={{ count: selectedIds.length }}
                            />
                        </Button>
                    </Popconfirm>
                )}
            </Space>
            {lastSync && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="success"
                    showIcon
                    icon={<MailOutlined />}
                    message={
                        <FormattedMessage
                            id="crm.inbox.syncResult"
                            defaultMessage="Sync complete — {fetched} fetched, {matched} matched, {flagged} flagged, {ignored} ignored, {skipped} already imported, {rematched} previously flagged now matched"
                            values={lastSync as any}
                        />
                    }
                    closable
                    onClose={() => setLastSync(null)}
                />
            )}
            <Table
                rowKey="id"
                size="small"
                loading={isFetching}
                dataSource={emails || []}
                columns={columns}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: (keys) => setSelectedIds(keys as number[]),
                }}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
            />

            {/* Assign to account */}
            <Modal
                open={!!assignTarget}
                title={<FormattedMessage id="crm.inbox.assignTitle" defaultMessage="Assign to account" />}
                okText={<FormattedMessage id="crm.inbox.assign" defaultMessage="Assign" />}
                okButtonProps={{ disabled: !assignCompanyId, loading: assignResult.isLoading }}
                onOk={handleAssign}
                onCancel={() => setAssignTarget(null)}
            >
                <div style={{ marginBottom: 12 }}>
                    <Text type="secondary">
                        {assignTarget?.fromEmail} — {assignTarget?.subject}
                    </Text>
                </div>
                <Select
                    style={{ width: '100%' }}
                    showSearch
                    filterOption={false}
                    loading={companiesLoading}
                    placeholder={intl.formatMessage({ id: 'crm.inbox.searchAccounts' })}
                    onSearch={setCompanySearch}
                    value={assignCompanyId ?? undefined}
                    onChange={(v) => setAssignCompanyId(v)}
                    options={(companySearchResults?.data || []).map((c) => ({
                        value: c.id,
                        label: c.name,
                    }))}
                />
            </Modal>

            {/* Read message */}
            <Modal
                open={!!viewTarget}
                width={720}
                title={viewTarget?.subject || ''}
                footer={[
                    <Button key="close" onClick={() => setViewTarget(null)}>
                        <FormattedMessage id="crm.inbox.close" defaultMessage="Close" />
                    </Button>,
                ]}
                onCancel={() => setViewTarget(null)}
            >
                {viewTarget && (
                    <>
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {viewTarget.fromName ? `${viewTarget.fromName} <${viewTarget.fromEmail}>` : viewTarget.fromEmail}
                                {' · '}
                                {new Date(viewTarget.receivedAt).toLocaleString()}
                                {(viewTarget.toJson?.length ?? 0) > 0 && (
                                    <> · <FormattedMessage id="crm.email.to" defaultMessage="To" />: {viewTarget.toJson!.join(', ')}</>
                                )}
                                {(viewTarget.ccJson?.length ?? 0) > 0 && (
                                    <> · <FormattedMessage id="crm.email.cc" defaultMessage="CC" />: {viewTarget.ccJson!.join(', ')}</>
                                )}
                            </Text>
                        </div>
                        <EmailBodyHtml
                            body={
                                showFullBody
                                    ? viewTarget.bodyHtml || viewTarget.bodyText || ''
                                    : viewTarget.strippedHtml || viewTarget.strippedText || viewTarget.bodyHtml || viewTarget.bodyText || ''
                            }
                            style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: 8,
                                padding: '12px 16px',
                                background: '#fafafa',
                                maxHeight: 400,
                                overflowY: 'auto',
                            }}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowFullBody(!showFullBody)}>
                                {showFullBody ? (
                                    <FormattedMessage id="crm.inbox.hideQuoted" defaultMessage="Hide quoted history" />
                                ) : (
                                    <FormattedMessage id="crm.inbox.showFull" defaultMessage="Show full message" />
                                )}
                            </Button>
                        </div>
                        {(viewTarget.attachmentsJson?.length ?? 0) > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <Space size={6} wrap>
                                    {viewTarget.attachmentsJson!.map((a, i) => (
                                        <Button
                                            key={i}
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            disabled={!a.s3Key}
                                            onClick={() => handleDownload(viewTarget, i)}
                                        >
                                            {a.filename} ({Math.round(a.size / 1024)} KB)
                                        </Button>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
};
